/**
 * Onboarding Questions Configuration
 *
 * Streamlined narrative flow:
 * 1. Welcome Story
 * 2. Financial Experience
 * 3. About You (countries + ageRange combined)
 * 4. Goal Selection (combined mission intro + goal cards)
 * 5. Plan Approval (UNTOUCHED)
 * 6. Bracelet Introduction (shows bracelet + awards Sophia S Silver + todos loading)
 * 7. Install Sophia (PWA install prompt)
 *
 * moneyStage, monthlySavings, and contactInstructions are collected via
 * cycle activities using <ProfileQuestion:...> inline chat components.
 */

export const FINANCIAL_EXPERIENCE_OPTIONS = [
  {
    value: 'beginner',
    label: 'I haven\'t invested yet',
    description: 'Ready to start your journey',
    image: 'seed.png'
  },
  {
    value: 'intermediate',
    label: 'I\'ve just made my first investment',
    description: 'You\'ve taken the first step',
    image: 'seedling.png'
  },
  {
    value: 'advanced',
    label: 'Investing isn\'t new to me',
    description: 'You have some experience',
    image: 'growing-plant.png'
  },
  {
    value: 'expert',
    label: 'I\'m actively managing investments',
    description: 'You\'re experienced in investing',
    image: 'tree.png'
  }
];

// Legacy exports kept for ProfileQuestion cycle activities that reference these
export const MONEY_STAGE_OPTIONS = [
  { value: 'exploring', label: 'Exploring my options' },
  { value: 'planning', label: 'Planning my next steps' },
  { value: 'acting', label: 'Taking action now' },
  { value: 'established', label: 'Managing my portfolio' }
];

export const MONTHLY_SAVINGS_OPTIONS = [
  { value: 0, label: 'I\'m not saving yet' },
  { value: 1, label: 'Under $100/month', image: 'money-0.png' },
  { value: 2, label: '$100-$500/month', image: 'money-1.png' },
  { value: 3, label: '$500-$1,000/month', image: 'money-2.png' },
  { value: 4, label: '$1,000-$2,000/month', image: 'money-3.png' },
  { value: 5, label: '$2,000-$5,000/month', image: 'money-4.png' },
  { value: 6, label: 'Over $5,000/month', image: 'money-5.png' }
];

export const PROFESSIONAL_ROLE_OPTIONS = [
  { value: 'corporate', label: 'Corporate' },
  { value: 'entrepreneur', label: 'Entrepreneur' },
  { value: 'not_employed', label: 'Not employed' }
];

export const GENDER_OPTIONS = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' }
];

export const AGE_RANGE_OPTIONS = [
  { value: '18-24', label: '18-24' },
  { value: '25-34', label: '25-34' },
  { value: '35-44', label: '35-44' },
  { value: '45-54', label: '45-54' },
  { value: '55-64', label: '55-64' },
  { value: '65+', label: '65+' }
];

export const COUNTRY_OPTIONS = [
  { value: 'Singapore', label: 'Singapore', currency: 'S$ (SGD)' },
  { value: 'Hong Kong', label: 'Hong Kong', currency: 'HK$ (HKD)' },
  { value: 'United Kingdom', label: 'United Kingdom', currency: '£ (GBP)' },
  { value: 'Australia', label: 'Australia', currency: 'A$ (AUD)' },
  { value: 'United States', label: 'United States', currency: '$ (USD)' }
];

/** Default currency used for custom/unlisted countries */
export const DEFAULT_CURRENCY = '$ (USD)';

/**
 * Get the currency label for a country.
 * Returns the matching COUNTRY_OPTIONS currency, or DEFAULT_CURRENCY for custom countries.
 */
export function getCurrencyForCountry(country) {
  const match = COUNTRY_OPTIONS.find(opt => opt.value === country);
  return match ? match.currency : DEFAULT_CURRENCY;
}

export const GOAL_OPTIONS = [
  { value: 'Building Emergency Savings', label: 'Build emergency savings', image: 'building-emergency-savings.png' },
  { value: 'Pay Down Debt', label: 'Pay down debt', image: 'pay-down-debt.png' },
  { value: 'Saving for Retirement', label: 'Save for retirement', image: 'saving-for-retirement.png' },
  { value: 'Budgeting and Managing Spending', label: 'Budget and manage spending', image: 'budgeting-and-managing-spending.png' },
  { value: 'Create an Investment Portfolio', label: 'Create an investment portfolio', image: 'create-an-investment-portfolio.png' },
  { value: 'Grow and Manage an Investment Portfolio', label: 'Grow your investments', image: 'grow-and-manage-investments.png' },
 // { value: 'Building and Maintaining Good Credit', label: 'Build good credit', image: 'building-good-credit.png' },
  { value: 'Planning for Health Care and Insurance', label: 'Plan health care & insurance', image: 'planning-health-care.png' },
//  { value: 'Supporting Dependents and Caregiving', label: 'Support dependents', image: 'supporting-dependents.png' },
  { value: 'Homeownership or Major Purchases', label: 'Save for a home or big purchase', image: 'homeownership-major-purchases.png' },
  { value: 'Start a Business or Side Hustle', label: 'Start a business or side hustle', image: 'start-a-business.png' },
 // { value: 'Estate and Legacy Planning', label: 'Plan your estate & legacy', image: 'estate-and-legacy-planning.png' },
  { value: 'Saving for a Child\'s Education', label: 'Save for education', image: 'saving-for-education.png' }
 // { value: 'Achieving Financial Wellbeing', label: 'Achieve financial wellbeing', image: 'achieving-financial-wellbeing.png' }
];

export const CONTACT_PREFS_OPTIONS = [
  { value: 'Morning (before 10am)', label: 'Morning (before 10am)' },
  { value: 'Midday (10am-2pm)', label: 'Midday (10am-2pm)' },
  { value: 'Afternoon (2pm-6pm)', label: 'Afternoon (2pm-6pm)' },
  { value: 'Evening (after 6pm)', label: 'Evening (after 6pm)' },
  { value: 'Anytime', label: 'Anytime' }
];

export const BARRIER_OPTIONS = [
  { value: "I'm afraid of losing money", label: "I'm afraid of losing money" },
  { value: "I don't have time", label: "I don't have time" },
  { value: "I don't know how", label: "I don't know how" },
  { value: "I don't have enough money", label: "I don't have enough money" },
  { value: "I never get around to it", label: "I never get around to it" },
  { value: 'Too many options overwhelm me', label: 'Too many options overwhelm me' },
  { value: 'No one around me to support me', label: 'No one around me to support me' },
  { value: 'Someone else does it for me', label: 'Someone else does it for me' },
  { value: "It doesn't feel like my priority", label: "It doesn't feel like my priority" },
  { value: "I've made money mistakes before", label: "I've made money mistakes before" },
  { value: 'I forget to do money stuff', label: 'I forget to do money stuff' }
];

export const onboardingFlow = [
  // Step 1: Welcome Story
  {
    id: 'welcome-intro',
    type: 'intro',
    title: 'Welcome to Sophia',
    description: 'Your personal investment coach.\nLet\'s set up your journey in just a few steps.',
  },
  // Step 2: Financial Experience
  {
    id: 'financialExperience',
    type: 'question',
    field: 'financialExperience',
    questionText: 'Where are you on your investment journey?',
    subheading: 'This selection helps us understand your level of investing',
    inputType: 'selection-cards',
    options: FINANCIAL_EXPERIENCE_OPTIONS
  },
  // Step 3: About You (countries + ageRange combined)
  {
    id: 'about-you',
    type: 'about-you',
    fields: ['countries', 'ageRange'],
  },
  // Step 4: Goal Selection (multi-select, up to 3). Priority is set on this
  // same screen — the selected goals show as an ordered 1..3 list with
  // up/down arrows — so there is no separate ranking step.
  {
    id: 'currentMission',
    type: 'question',
    field: 'currentMission',
    questionText: 'What matters most to you right now?',
    subheading: 'Pick up to 3 goals in order of importance. This helps shape your plan.',
    inputType: 'goal-cards',
    maxSelections: 3,
    options: GOAL_OPTIONS,
    placeholder: 'Or describe your own goal...',
    allowCustom: true
  },
  // Step 5c: Barriers — informs both plan generation and the per-conversation
  // system prompt so the MI coach can speak to the user's self-reported blockers.
  {
    id: 'barriers',
    type: 'question',
    field: 'barriers',
    questionText: "What's getting in your way?",
    subheading: 'Pick all that apply — These selections will shape our conversations and tailor our guidance to you.',
    inputType: 'chip-multiple',
    options: BARRIER_OPTIONS,
    placeholder: 'Or write your own…',
    allowCustom: true,
    required: true
  },
  // Step 6: Plan Approval (UNTOUCHED)
  {
    id: 'plan-approval',
    type: 'plan-approval',
  },
  // Step 6b: WhatsApp notifications opt-in (just before PWA install).
  // Captures the user's WhatsApp number and triggers a verification message.
  // The user can skip and link later from Settings.
  {
    id: 'whatsapp-opt-in',
    type: 'whatsapp-opt-in',
  },
  // Step 7: Install Sophia (PWA)
  {
    id: 'setup',
    type: 'intro',
    educationType: 'pwa'
  }
];

/**
 * Get the change-objective mini-flow (mission screens only)
 * Used when user changes their financial goal from chat
 * @returns {Array} - Array of mission-related onboarding items
 */
export function getChangeObjectiveFlow() {
  return [
    {
      id: 'currentMission',
      type: 'question',
      field: 'currentMission',
      questionText: 'What is your new financial goal?',
      subheading: 'Pick up to 3 goals in order of importance. This helps shape your plan.',
      inputType: 'goal-cards',
      maxSelections: 3,
      options: GOAL_OPTIONS,
      placeholder: 'Or describe your own goal...',
      allowCustom: true
    },
    {
      id: 'barriers',
      type: 'question',
      field: 'barriers',
      questionText: "What's getting in your way?",
      subheading: 'Pick all that apply — These selections will shape our conversations and tailor our guidance to you.',
      inputType: 'chip-multiple',
      options: BARRIER_OPTIONS,
      placeholder: 'Or write your own…',
      allowCustom: true,
      required: true
    },
    {
      id: 'plan-approval',
      type: 'plan-approval',
    },
    // Final step: same notifications screen as initial onboarding, so both
    // flows converge on the same ending. Plan approval is already synchronous
    // (PlanApprovalScreen awaits todosAPI.approvePlan), so we don't need a
    // separate todos-loading polling screen here.
    {
      id: 'setup',
      type: 'intro',
      educationType: 'pwa'
    }
  ];
}

/**
 * Get questions that need to be answered based on user data.
 *
 * Gates on: financialExperience, countries, ageRange, professionalRole, gender, hasActivePlan.
 * moneyStage and monthlySavings are collected via cycle activities.
 *
 * @param {Object} user - User object from Strapi
 * @returns {Array} - Array of onboarding items that need to be shown
 */
export function getUnansweredQuestions(user) {
  const unansweredFields = [];

  if (!user.financialExperience) {
    unansweredFields.push('financialExperience');
  }
  if (!user.countries) {
    unansweredFields.push('countries');
  }
  if (!user.ageRange) {
    unansweredFields.push('ageRange');
  }
  if (!user.professionalRole) {
    unansweredFields.push('professionalRole');
  }
  if (!user.gender) {
    unansweredFields.push('gender');
  }
  if (user.hasActivePlan === false) {
    unansweredFields.push('currentMission');
    unansweredFields.push('barriers');
  }

  // WhatsApp opt-in is its own thing: not a profile field, but worth surfacing
  // to existing users who never saw the prompt. Honor a localStorage
  // dismissal flag so a user who explicitly Skipped doesn't get re-asked on
  // every login (same pattern the PWA install prompt uses).
  let whatsappDismissed = false;
  try {
    whatsappDismissed = typeof window !== 'undefined'
      && window.localStorage?.getItem('sophia.whatsappOnboardingDismissed') === '1';
  } catch (_) {
    // localStorage can throw in privacy mode; treat as "not dismissed".
  }
  // Only prompt for WhatsApp when the server has it enabled (ENABLE_WHATSAPP)
  // AND the bot is currently online (whatsappConnected). Both come from
  // /api/auth/me; if absent (older server) treat as disabled so we never prompt
  // for an undeliverable channel. Gating on live connectivity means a bot outage
  // hides the step rather than sending the user to a "check your WhatsApp"
  // screen for a verification message that was never delivered — it stays
  // unfulfilled and reappears on a later login once the bot is back up.
  const needsWhatsApp = user.whatsappEnabled === true
    && user.whatsappConnected === true
    && !user.whatsappOptIn
    && !whatsappDismissed;

  // If nothing is unanswered AND WhatsApp doesn't need prompting, skip
  // onboarding entirely.
  if (unansweredFields.length === 0 && !needsWhatsApp) {
    return [];
  }

  console.log('[Onboarding Flow] Building flow:');
  console.log('  - Unanswered fields:', unansweredFields);
  console.log('  - user.hasActivePlan:', user.hasActivePlan);

  // Build the flow from the onboardingFlow array
  const flowToShow = [];
  const needsFullOnboarding = unansweredFields.includes('financialExperience');
  const needsActivePlan = user.hasActivePlan === false;
  const needsAboutYou = unansweredFields.includes('countries') || unansweredFields.includes('ageRange') || unansweredFields.includes('professionalRole') || unansweredFields.includes('gender');

  for (const item of onboardingFlow) {
    if (item.type === 'intro') {
      if (item.id === 'welcome-intro') {
        if (needsFullOnboarding) {
          flowToShow.push(item);
        }
      } else if (item.educationType === 'pwa') {
        // Always show PWA install screen for everyone
        flowToShow.push(item);
      }
    } else if (item.type === 'about-you') {
      // Show combined countries+ageRange screen if either is missing
      if (needsAboutYou) {
        flowToShow.push(item);
      }
    } else if (item.type === 'question' && unansweredFields.includes(item.field)) {
      flowToShow.push(item);
    } else if (item.type === 'plan-approval' && needsActivePlan) {
      flowToShow.push(item);
    } else if (item.type === 'whatsapp-opt-in') {
      // Show WhatsApp opt-in only when the server feature flag is on, the bot
      // is currently online, AND the user hasn't already confirmed it. Gating on
      // live connectivity (whatsappConnected) means the step is hidden during a
      // bot outage instead of promising a verification message we can't send;
      // it stays unfulfilled and reappears once the bot is back. The skip path
      // is non-destructive (no number stored), so re-asking after a skip is fine.
      if (user.whatsappEnabled === true && user.whatsappConnected === true && !user.whatsappOptIn) {
        flowToShow.push(item);
      }
    }
  }

  console.log('[Onboarding Flow] Final flow items:', flowToShow.map(item => `${item.type}:${item.id || item.field}`));
  console.log('[Onboarding Flow] Total items:', flowToShow.length);

  return flowToShow;
}
